/* ============================================================================
   Sample data - used in preview mode (before Firebase is connected) and as a
   reference for the Firestore document shapes.

   Firestore collections this mirrors:
     products  { name, price_in_credits, image_url, description, link, category, master_exclusive? }
     users     { email, credit_balance, display_name, subscription_tier, subscription_next_charge, first_credit_bonus_pending?, created_at }
     purchases { user_id, product_id, timestamp }
     kofi_events { event_id, type, email, tier, credits_added, processed_at }
   ============================================================================ */

export const SAMPLE_USER = {
  display_name: "Author",
  email: "you@example.com",
  credit_balance: 180,
  subscription_tier: "batch",
  subscription_next_charge: "2026-08-10",
};

export const KOFI_URL = "https://ko-fi.com/elzbiet/tiers";
/** Members get a one-use invite via getDiscordInvite. No public permanent invite. */
export const DISCORD_URL = "";
export const EPISODE_URL = "https://www.episodeinteractive.com";
export const INSTAGRAM_URL = "https://www.instagram.com/elzbiet.zaleski";

/** Launch promo: first paid deposit is doubled until FIRST_CREDIT_BONUS_ENDS_AT. */
export const FIRST_CREDIT_BONUS = true;

/** Monthly credit deposit per tier (applied by Ko-fi webhook on charge). */
export const TIER_CREDITS = {
  free: 0,
  dough: 30,
  batch: 100,
  master: 220,
};

/** Custom overlay requests per calendar month (no rollover). Keep in sync with functions/overlays.js */
export const TIER_OVERLAY_QUOTA = {
  free: 0,
  dough: 1,
  batch: 3,
  master: 5,
};

/**
 * One-time 2× first refill ends at this instant (exclusive).
 * Through end of 10 Oct 2026 UTC  -  keep in sync with functions/index.js.
 */
export const FIRST_CREDIT_BONUS_ENDS_AT = "2026-10-11T00:00:00.000Z";

export function isFirstCreditBonusActive(now = new Date()) {
  return now.getTime() < Date.parse(FIRST_CREDIT_BONUS_ENDS_AT);
}

/**
 * Map Ko-fi membership tier labels or monthly amounts (EUR) → internal tier IDs.
 * Used by functions/index.js - keep in sync when renaming Ko-fi tiers.
 */
export const KOFI_TIER_MAP = {
  "The Cookie Dough": "dough",
  "Cookie Dough": "dough",
  "The Cookie Batch": "batch",
  "Cookie Batch": "batch",
  "The Sugar Rush": "master",
  "Sugar Rush": "master",
  "The Cookie Crown": "master",
  "Cookie Crown": "master",
  "The Master Baker": "master",
  "Master Baker": "master",
  "test tier": "master",
  "Test Tier": "master",
  "5": "dough",
  "10": "batch",
  "12": "batch",
  "20": "master",
  "25": "master",
  "28": "master",
  "1": "master",
};

/* Kofi membership tiers - informational only; billing happens on Ko-fi. */
export const SUBSCRIPTION_TIERS = [
  {
    id: "free",
    name: "The Crumb",
    price: "Free",
    billing: "No recurring charge",
    vault: "None",
    perks: [
      "Browse Asset Jar",
      "Unlock free Originals",
      "Baking List votes",
      "Peek at Lookbook",
    ],
    monthlyCredits: 0,
  },
  {
    id: "dough",
    name: "The Cookie Dough",
    price: "€5 / month",
    billing: "Monthly on Ko-fi",
    vault: "30 credits / month",
    overlayRequests: "1 / month",
    perks: [
      "Lookbook + Members Pantry",
      "First refill: 2× credits (through 10 Oct 2026)",
    ],
    kofi: KOFI_URL,
    monthlyCredits: 30,
  },
  {
    id: "batch",
    name: "The Cookie Batch",
    price: "€12 / month",
    billing: "Monthly on Ko-fi",
    vault: "100 credits / month",
    overlayRequests: "3 / month",
    lottery: "1 entry / month",
    earlyLottery: "1 entry",
    perks: [
      "Lookbook + Members Pantry",
      "First refill: 2× credits (through 10 Oct 2026)",
    ],
    kofi: KOFI_URL,
    monthlyCredits: 100,
  },
  {
    id: "master",
    name: "The Sugar Rush",
    price: "€25 / month",
    billing: "Monthly on Ko-fi",
    vault: "220 credits / month",
    overlayRequests: "5 / month (priority)",
    lottery: "2 entries / month",
    earlyLottery: "2 entries",
    perks: [
      "Lookbook + Members Pantry",
      "First refill: 2× credits (through 10 Oct 2026)",
    ],
    kofi: KOFI_URL,
    monthlyCredits: 220,
  },
];

/** Real vault listings only - skip placeholder rows without a preview image. */
export const SAMPLE_PRODUCTS = [
  {
    id: "ov-phone-overlays",
    name: "Phone Overlay Pack",
    price_in_credits: 10,
    category: "Overlays",
    description:
      "4 phone styles × 6 skin tones. Drop the overlays into Episode.",
    image_url: "downloads/phone-overlays.webp",
    link: "#",
    listed_at: "2026-01-05T12:00:00.000Z",
    asset_contents: { overlays: 24 },
  },
  {
    id: "tpl-phone-gallery",
    name: "Phone Gallery Template",
    price_in_credits: 25,
    category: "Templates",
    description:
      "Scrollable 5-photo phone gallery with overlays plus copy-paste code.",
    image_url: "downloads/phone-gallery.webp",
    link: "#",
    listed_at: "2026-07-21T12:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: 3 },
  },
  {
    id: "tpl-wedding-ceremony-seats-front",
    name: "Ceremony Seats Front Setup",
    price_in_credits: 5,
    category: "Setups",
    description:
      "Paste-in code that seats characters on the ceremony seats front background.",
    image_url: "downloads/wedding-ceremony-seats-front-template.webp",
    link: "#",
    listed_at: "2026-01-06T12:00:00.000Z",
    has_episode_code: true,
    has_vault_download: false,
    asset_contents: { code: true },
    related_vault_product: {
      id: "bg-rustic-wedding",
      label: "Rustic Wedding Background Set",
      note: "Needs the Rustic Wedding backgrounds/overlays for the full scene.",
    },
  },
  {
    id: "bg-bedroom",
    name: "Bedroom",
    price_in_credits: 15,
    category: "Backgrounds",
    description:
      "One room, several looks. Pick the bedroom you want  -  day + night versions and overlays come with it.",
    image_url: "downloads/bedroom_1.webp",
    preview_images: [
      "downloads/bedroom_1.webp",
      "downloads/bedroom_2.webp",
      "downloads/bedroom_3.webp",
      "downloads/bedroom_4.webp",
      "downloads/bedroom_5.webp",
      "downloads/bedroom_6.webp",
    ],
    link: "#",
    listed_at: "2026-08-18T20:00:00.000Z",
    has_vault_download: true,
    asset_contents: { backgrounds: 2, overlays: true },
    variants: [
      {
        id: "1_bedroom_white_penthouse",
        name: "White Penthouse",
        image_url: "downloads/bedroom_1.webp",
        storage_path: "vault/1_bedroom_white_penthouse.zip",
      },
      {
        id: "2_bedroom_royal_castle_blue",
        name: "Royal Blue",
        image_url: "downloads/bedroom_2.webp",
        storage_path: "vault/2_bedroom_royal_castle_blue.zip",
      },
      {
        id: "3_bedroom_penthouse",
        name: "Penthouse",
        image_url: "downloads/bedroom_3.webp",
        storage_path: "vault/3_bedroom_penthouse.zip",
      },
      {
        id: "4_bedroom_white_w_fireplace",
        name: "White with Fireplace",
        image_url: "downloads/bedroom_4.webp",
        storage_path: "vault/4_bedroom_white_w_fireplace.zip",
      },
      {
        id: "5_mint_bedroom",
        name: "Mint",
        image_url: "downloads/bedroom_5.webp",
        storage_path: "vault/5_mint_bedroom.zip",
      },
      {
        id: "6_guys_bedroom",
        name: "Red",
        image_url: "downloads/bedroom_6.webp",
        storage_path: "vault/6_guys_bedroom.zip",
      },
    ],
  },
  {
    id: "bg-living-room",
    name: "Living Room",
    price_in_credits: 15,
    category: "Backgrounds",
    description:
      "One room, several looks. Pick the living room you want  -  day + night versions and overlays come with it.",
    image_url: "downloads/lr_1.webp",
    preview_images: [
      "downloads/lr_1.webp",
      "downloads/lr_2.webp",
      "downloads/lr_3.webp",
      "downloads/lr_4.webp",
      "downloads/lr_5.webp",
      "downloads/lr_6.webp",
    ],
    link: "#",
    listed_at: "2026-08-18T16:00:00.000Z",
    has_vault_download: true,
    asset_contents: { backgrounds: 2, overlays: true },
    variants: [
      {
        id: "1_white_penthouse",
        name: "White Penthouse",
        image_url: "downloads/lr_1.webp",
        storage_path: "vault/1_white_penthouse.zip",
      },
      {
        id: "2_countryside",
        name: "Countryside",
        image_url: "downloads/lr_2.webp",
        storage_path: "vault/2_countryside.zip",
      },
      {
        id: "3_beach_mansion",
        name: "Beach Mansion",
        image_url: "downloads/lr_3.webp",
        storage_path: "vault/3_beach_mansion.zip",
      },
      {
        id: "4_penthouse",
        name: "Penthouse",
        image_url: "downloads/lr_4.webp",
        storage_path: "vault/4_penthouse.zip",
      },
      {
        id: "5_japanese",
        name: "Japanese",
        image_url: "downloads/lr_5.webp",
        storage_path: "vault/5_japanese.zip",
      },
      {
        id: "6_gamers",
        name: "Gamer",
        image_url: "downloads/lr_6.webp",
        storage_path: "vault/6_gamers.zip",
      },
    ],
  },
  {
    id: "bg-exteriors",
    name: "Exteriors Set",
    price_in_credits: 80,
    category: "Backgrounds",
    description:
      "Outdoor scenes  -  13 backgrounds + 30 overlays + 2 looping backgrounds.",
    image_url: "downloads/exteriors_1.webp",
    preview_images: [
      "downloads/exteriors_1.webp",
      "downloads/exteriors_2.webp",
    ],
    link: "#",
    listed_at: "2026-07-27T12:00:00.000Z",
    asset_contents: { backgrounds: 13, overlays: 30, looping_backgrounds: 2 },
  },
  {
    id: "bg-japanese-apartment",
    name: "Japanese Apartment Set",
    price_in_credits: 75,
    category: "Backgrounds",
    description:
      "Japanese apartment interiors  -  11 backgrounds + 18 overlays.",
    image_url: "downloads/japanese_1.webp",
    preview_images: [
      "downloads/japanese_1.webp",
      "downloads/japanese_2.webp",
    ],
    link: "#",
    listed_at: "2026-08-06T12:00:00.000Z",
    asset_contents: { backgrounds: 11, overlays: 18 },
  },
  {
    id: "bg-cabin-house",
    name: "Cabin House Set",
    price_in_credits: 85,
    category: "Backgrounds",
    description:
      "Cozy cabin rooms and woods  -  10 backgrounds + 34 overlays.",
    image_url: "downloads/cabin_house_1.webp",
    preview_images: [
      "downloads/cabin_house_1.webp",
      "downloads/cabin_house_2.webp",
    ],
    link: "#",
    listed_at: "2026-07-26T12:00:00.000Z",
    asset_contents: { backgrounds: 10, overlays: 34 },
  },
  {
    id: "bg-beach-mansion",
    name: "Beach Mansion Set",
    price_in_credits: 90,
    category: "Backgrounds",
    description:
      "Coastal mansion interiors and grounds  -  16 backgrounds + 67 overlays.",
    image_url: "downloads/beach_mansion_1.webp",
    preview_images: [
      "downloads/beach_mansion_1.webp",
      "downloads/beach_mansion_2.webp",
    ],
    link: "#",
    listed_at: "2026-07-25T12:00:00.000Z",
    asset_contents: { backgrounds: 16, overlays: 67 },
  },
  {
    id: "bg-cafes-restaurants",
    name: "Cafes & Restaurants Set",
    price_in_credits: 80,
    category: "Backgrounds",
    description:
      "Cozy cafes and restaurant spaces  -  14 backgrounds + 34 overlays.",
    image_url: "downloads/cafes_set_1.webp",
    preview_images: [
      "downloads/cafes_set_1.webp",
      "downloads/cafes_set_2.webp",
    ],
    link: "#",
    listed_at: "2026-07-28T12:00:00.000Z",
    asset_contents: { backgrounds: 14, overlays: 34 },
  },
  {
    id: "bg-beach-resort",
    name: "Beach Resort Set",
    price_in_credits: 30,
    category: "Backgrounds",
    description:
      "Sun, sand, and resort vibes  -  14 backgrounds + 38 overlays.",
    image_url: "downloads/beach_resort_1.webp",
    preview_images: [
      "downloads/beach_resort_1.webp",
      "downloads/beach_resort_2.webp",
    ],
    link: "#",
    listed_at: "2026-07-30T12:00:00.000Z",
    asset_contents: { backgrounds: 14, overlays: 38 },
  },
  {
    id: "bg-high-school-remastered",
    name: "High School Set (Remastered)",
    price_in_credits: 130,
    category: "Backgrounds",
    description:
      "Campus remaster  -  classrooms, labs, halls, and more. 19 backgrounds + 102 overlays.",
    image_url: "downloads/high_school_set_1.webp",
    preview_images: [
      "downloads/high_school_set_1.webp",
      "downloads/high_school_set_2.webp",
      "downloads/high_school_set_3.webp",
    ],
    link: "#",
    listed_at: "2026-08-18T12:00:00.000Z",
    asset_contents: { backgrounds: 19, overlays: 102 },
  },
  {
    id: "bg-music-tour",
    name: "Music Tour Background Set",
    price_in_credits: 115,
    category: "Backgrounds",
    description:
      "Tour life: stage, backstage, hotel, bus with 15 backgrounds + overlays.",
    image_url: "downloads/musictourset.webp",
    link: "https://drive.google.com/file/d/1-FhXpQ7PepST3rvzKhgal3k8rFR047EA/view?usp=drive_link",
    listed_at: "2026-08-17T12:00:00.000Z",
    asset_contents: { backgrounds: 15, overlays: 62 },
  },
  {
    id: "bg-b99-precinct",
    name: "Police Precinct Background Set",
    price_in_credits: 75,
    category: "Backgrounds",
    description:
      "Bullpen, interrogation, break room, and more with sitcom-style precinct vibes.",
    image_url: "downloads/b99.webp",
    link: "#",
    listed_at: "2026-07-18T12:00:00.000Z",
    asset_contents: { backgrounds: 18, overlays: 151 },
  },
  {
    id: "bg-office-set",
    name: "Office Background Set",
    price_in_credits: 100,
    category: "Backgrounds",
    description:
      "Open floor, conference room, break room, reception, corner office.",
    image_url: "downloads/office-set.webp",
    link: "#",
    listed_at: "2026-07-24T12:00:00.000Z",
  },
  {
    id: "bg-rustic-wedding",
    name: "Rustic Wedding Background Set",
    price_in_credits: 90,
    category: "Backgrounds",
    description:
      "Ceremony, reception, garden arches, rustic wedding spaces.",
    image_url: "downloads/wedding-set.webp",
    link: "#",
    listed_at: "2026-07-29T12:00:00.000Z",
    asset_contents: { backgrounds: 10, overlays: 39 },
    related_vault_product: {
      id: "tpl-wedding-ceremony-seats-front",
      label: "Ceremony Seats Front Setup",
      note: "Character seating code for the ceremony seats front background.",
    },
  },
  {
    id: "bg-bookstore",
    name: "Bookstore Background Set",
    price_in_credits: 90,
    category: "Backgrounds",
    description:
      "Cozy shelves, reading nooks, and cafe corners  -  12 backgrounds + 62 overlays.",
    image_url: "downloads/boostore.png",
    link: "#",
    listed_at: "2026-08-08T12:00:00.000Z",
    asset_contents: { backgrounds: 12, overlays: 62 },
  },
  {
    id: "bg-countryside-house",
    name: "Countryside House Background Set",
    price_in_credits: 95,
    category: "Backgrounds",
    description:
      "Rural home interiors and grounds  -  11 backgrounds + 46 overlays.",
    image_url: "downloads/countryside_house.webp",
    link: "#",
    listed_at: "2026-08-07T12:00:00.000Z",
    asset_contents: { backgrounds: 11, overlays: 46 },
  },
  {
    id: "bg-boho-apartment",
    name: "Boho Apartment Set",
    price_in_credits: 100,
    category: "Backgrounds",
    description:
      "Warm boho apartment rooms  -  12 backgrounds + 61 overlays.",
    image_url: "downloads/boho_set_1.webp",
    preview_images: ["downloads/boho_set_1.webp", "downloads/boho_set_2.webp"],
    link: "#",
    listed_at: "2026-07-22T12:00:00.000Z",
    asset_contents: { backgrounds: 12, overlays: 61 },
    related_vault_product: {
      id: "bg-boho-apartment-christmas",
      label: "Christmas Boho Apartment Set",
      note: "Own either Boho apartment set for 70% off the other.",
    },
  },
  {
    id: "bg-boho-apartment-christmas",
    name: "Christmas Boho Apartment Set",
    price_in_credits: 100,
    category: "Backgrounds",
    description:
      "The boho apartment dressed for the holidays  -  12 backgrounds + 61 overlays.",
    image_url: "downloads/boho_set_xmas_1.webp",
    preview_images: [
      "downloads/boho_set_xmas_1.webp",
      "downloads/boho_set_xmas_2.webp",
    ],
    link: "#",
    listed_at: "2026-07-23T12:00:00.000Z",
    asset_contents: { backgrounds: 12, overlays: 61 },
    related_vault_product: {
      id: "bg-boho-apartment",
      label: "Boho Apartment Set",
      note: "Own either Boho apartment set for 70% off the other.",
    },
  },
  {
    id: "bg-college-dormitory",
    name: "College Dormitory Set",
    price_in_credits: 90,
    category: "Backgrounds",
    description:
      "Campus dorm life  -  rooms, halls, and study corners. 10 backgrounds + 40 overlays.",
    image_url: "downloads/dorm_1.webp",
    preview_images: ["downloads/dorm_1.webp", "downloads/dorm_2.webp"],
    link: "#",
    listed_at: "2026-08-09T12:00:00.000Z",
    asset_contents: { backgrounds: 10, overlays: 40 },
  },
  {
    id: "bg-hospital",
    name: "Hospital Set",
    price_in_credits: 100,
    category: "Backgrounds",
    description:
      "Wards, waiting rooms, and clinic halls  -  16 backgrounds + 93 overlays.",
    image_url: "downloads/hospital_1.webp",
    preview_images: ["downloads/hospital_1.webp", "downloads/hospital_2.webp"],
    link: "#",
    listed_at: "2026-07-20T12:00:00.000Z",
    asset_contents: { backgrounds: 16, overlays: 93 },
  },
  {
    id: "bg-musician-apartment",
    name: "Musician Apartment Set",
    price_in_credits: 75,
    category: "Backgrounds",
    description:
      "A lived-in musician’s flat  -  practice corners and cozy rooms. 9 backgrounds + 21 overlays.",
    image_url: "downloads/musician_apartment_1.webp",
    preview_images: [
      "downloads/musician_apartment_1.webp",
      "downloads/musician_apartment_2.webp",
    ],
    link: "#",
    listed_at: "2026-08-01T12:00:00.000Z",
    asset_contents: { backgrounds: 9, overlays: 21 },
  },
  {
    id: "bg-music-production",
    name: "Music Production Set",
    price_in_credits: 80,
    category: "Backgrounds",
    description:
      "Studio booths, desks, and gear-filled rooms  -  13 backgrounds + 52 overlays.",
    image_url: "downloads/music_production_set.webp",
    link: "#",
    listed_at: "2026-08-16T12:00:00.000Z",
    asset_contents: { backgrounds: 13, overlays: 52 },
  },
  {
    id: "bg-gaming-set",
    name: "Gaming Set",
    price_in_credits: 60,
    category: "Backgrounds",
    description:
      "Setup rooms, RGB desks, and streamer corners  -  10 backgrounds + 47 overlays.",
    image_url: "downloads/gaming_set_1.webp",
    preview_images: [
      "downloads/gaming_set_1.webp",
      "downloads/gaming_set_2.webp",
    ],
    link: "#",
    listed_at: "2026-08-05T12:00:00.000Z",
    asset_contents: { backgrounds: 10, overlays: 47 },
  },
  {
    id: "bg-white-penthouse",
    name: "White Penthouse",
    price_in_credits: 80,
    category: "Backgrounds",
    description:
      "Bright marble rooms, kitchen, and terrace  -  a white penthouse with overlays.",
    image_url: "downloads/white_penthouse_1.webp",
    preview_images: [
      "downloads/white_penthouse_1.webp",
      "downloads/white_penthouse_2.webp",
    ],
    link: "#",
    listed_at: "2026-08-03T12:00:00.000Z",
    related_vault_product: {
      id: "bg-white-penthouse-christmas",
      label: "White Penthouse Christmas Edition",
      note: "Own either White Penthouse set for 70% off the other.",
    },
  },
  {
    id: "bg-white-penthouse-christmas",
    name: "White Penthouse Christmas Edition",
    price_in_credits: 80,
    category: "Backgrounds",
    description:
      "The white penthouse dressed for the holidays  -  snow, trees, and overlays.",
    image_url: "downloads/white_penthouse_xmas_1.webp",
    preview_images: [
      "downloads/white_penthouse_xmas_1.webp",
      "downloads/white_penthouse_xmas_2.webp",
    ],
    link: "#",
    listed_at: "2026-08-04T12:00:00.000Z",
    related_vault_product: {
      id: "bg-white-penthouse",
      label: "White Penthouse",
      note: "Own either White Penthouse set for 70% off the other.",
    },
  },
  {
    id: "bg-penthouse-set",
    name: "Penthouse Set",
    price_in_credits: 100,
    category: "Backgrounds",
    description:
      "City penthouse rooms, terraces, and views  -  16 backgrounds + 46 overlays.",
    image_url: "downloads/penthouse_1.webp",
    preview_images: [
      "downloads/penthouse_1.webp",
      "downloads/penthouse_2.webp",
      "downloads/penthouse_3.webp",
    ],
    link: "#",
    listed_at: "2026-07-31T12:00:00.000Z",
    asset_contents: { backgrounds: 16, overlays: 46 },
  },
  {
    id: "bg-gamer-apartment",
    name: "Gamer Apartment",
    price_in_credits: 70,
    category: "Backgrounds",
    description:
      "RGB setups, lounge rooms, and streamer corners  -  11 backgrounds + 34 overlays.",
    image_url: "downloads/gamer_apartment.webp",
    preview_images: ["downloads/gamer_apartment.webp"],
    link: "#",
    listed_at: "2026-08-02T12:00:00.000Z",
    asset_contents: { backgrounds: 11, overlays: 34 },
  },
  {
    id: "bg-night-club",
    name: "Night Club Set",
    price_in_credits: 75,
    category: "Backgrounds",
    description:
      "Neon club interiors, bar, and dance floor  -  9 backgrounds + 41 overlays.",
    image_url: "downloads/night_club_1.webp",
    preview_images: [
      "downloads/night_club_1.webp",
      "downloads/night_club_2.webp",
    ],
    link: "#",
    listed_at: "2026-07-19T12:00:00.000Z",
    asset_contents: { backgrounds: 9, overlays: 41 },
  },
  {
    id: "bg-royal-castle",
    name: "Royal Castle Set",
    price_in_credits: 90,
    category: "Backgrounds",
    description:
      "Royal halls, bedrooms, gardens, and more  -  15 backgrounds + 39 overlays. Winter season included.",
    image_url: "downloads/royal_castle_1.webp",
    preview_images: [
      "downloads/royal_castle_1.webp",
      "downloads/royal_castle_2.webp",
      "downloads/royal_castle_3.webp",
    ],
    link: "#",
    listed_at: "2026-08-24T20:00:00.000Z",
    asset_contents: { backgrounds: 15, overlays: 39 },
  },
  {
    id: "mg-signature-cocktail",
    name: "Signature Cocktail Mini-Game",
    price_in_credits: 35,
    category: "Mini-games",
    description:
      "Readers pick a drink base, mixer, and garnish and get a named cocktail. Overlays + directing code.",
    image_url: "downloads/signature_cocktail_mini_game.mp4",
    link: "#",
    listed_at: "2026-08-17T18:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: true },
    related_vault_product: {
      id: "mg-cocktail-challenge",
      label: "Cocktail Challenge Mini-Game",
      note: "Own either cocktail mini-game for 20% off the other.",
    },
  },
  {
    id: "mg-cocktail-challenge",
    name: "Cocktail Challenge Mini-Game",
    price_in_credits: 35,
    category: "Mini-games",
    description:
      "Readers mix a drink base, mixer, and garnish. Good combos win; bad ones flop. Overlays + directing code.",
    image_url: "downloads/cocktail_challenge_minigame_code.mp4?v=20260805c",
    link: "#",
    listed_at: "2026-08-17T20:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: true },
    related_vault_product: {
      id: "mg-signature-cocktail",
      label: "Signature Cocktail Mini-Game",
      note: "Own either cocktail mini-game for 20% off the other.",
    },
  },
  {
    id: "mg-phone-passcode",
    name: "Phone Passcode Mini-Game",
    price_in_credits: 15,
    category: "Mini-games",
    description:
      "Readers tap a phone keypad to crack a 4-digit passcode. Wrong code shakes; right code unlocks. Overlays + directing code.",
    image_url: "downloads/phone_passcode.mp4?v=20260817o",
    link: "#",
    listed_at: "2026-08-13T12:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: true },
  },
  {
    id: "mg-dressing-game",
    name: "Dressing Game",
    price_in_credits: 15,
    category: "Mini-games",
    description:
      "Readers browse three outfit options, preview with a zoom, then confirm or change their mind. Overlays + directing code.",
    image_url: "downloads/dressing_game_1.mp4?v=20260817o",
    link: "#",
    listed_at: "2026-08-12T12:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: true },
  },
  {
    id: "mg-wardrobe-minigame",
    name: "Wardrobe Mini-Game",
    price_in_credits: 15,
    category: "Mini-games",
    description:
      "Readers browse three looks in each of six wardrobe categories (everyday, casual, date, party, and more). Overlays + directing code.",
    image_url: "downloads/wardrobe_minigame.mp4?v=20260818c",
    link: "#",
    listed_at: "2026-08-15T12:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: true },
  },
  {
    id: "mg-wedding-outfit",
    name: "Wedding Outfit Mini-Game",
    price_in_credits: 15,
    category: "Mini-games",
    description:
      "Readers pick a wedding dress, accessories, and headpiece. Browse with arrows, then tap Finish when the look is ready. Overlays + directing code.",
    image_url: "downloads/wedding_outfit.mp4?v=20260818c",
    link: "#",
    listed_at: "2026-08-14T12:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: true },
  },
    {
    id: "tpl-tumbleweed",
    name: "Tumbleweed Rolling",
    price_in_credits: 0,
    category: "Templates",
    description:
      "Free mini template: one tumbleweed overlay that rolls across the screen, plus paste-in directing code.",
    image_url: "downloads/tumbleweed.mp4?v=20260806g",
    link: "#",
    listed_at: "2025-12-04T12:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: 1 },
  },
  {
    id: "tpl-cat-cc",
    name: "Cat Visual Customization",
    price_in_credits: 35,
    category: "Templates",
    description:
      "Readers pick a cat body type and coat, then tap Finish. Overlays + directing code.",
    image_url: "downloads/cat_cc.mp4?v=20260819m",
    link: "#",
    listed_at: "2026-08-10T12:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: true },
  },
  {
    id: "tpl-dog-cc",
    name: "Dog Visual Customization",
    price_in_credits: 35,
    category: "Templates",
    description:
      "Readers pick a dog breed and coat, then tap Finish. Overlays + directing code.",
    image_url: "downloads/dog_cc.mp4?v=20260819m",
    link: "#",
    listed_at: "2026-08-11T12:00:00.000Z",
    has_episode_code: true,
    asset_contents: { code: true, overlays: true },
  },
  {
    id: "ov-car-interior",
    name: "Car Interior Overlays",
    price_in_credits: 0,
    category: "Overlays",
    description: "5 car interior overlays for Episode scenes.",
    image_url: "downloads/car_interior.png",
    link: "#",
    listed_at: "2025-12-05T12:00:00.000Z",
    asset_contents: { overlays: 5 },
  },
  {
    id: "ov-mermaid-tails",
    name: "Mermaid Tails Overlays",
    price_in_credits: 0,
    category: "Overlays",
    description: "108 overlays across 19 poses. Mermaid tail overlays for Episode.",
    image_url: "downloads/mermaid_tails.png",
    link: "#",
    listed_at: "2025-12-06T12:00:00.000Z",
    asset_contents: { overlays: 108 },
  },
  {
    id: "ov-spaceships",
    name: "Spaceships Overlays",
    price_in_credits: 0,
    category: "Overlays",
    description: "13 spaceship overlays for Episode scenes.",
    image_url: "downloads/spaceships.png",
    link: "#",
    listed_at: "2025-12-07T12:00:00.000Z",
    asset_contents: { overlays: 13 },
  },
  {
    id: "ov-female-limb-overlays",
    name: "Female Limb Overlays",
    price_in_credits: 0,
    members_pantry: true,
    category: "Overlays",
    description: "22 poses, each in 55 skin tones. Limelight limb overlays for female characters.",
    image_url: "downloads/female_limbs.png",
    preview_images: [
      "downloads/female_limbs.png",
      "downloads/limb_guide_1.webp",
      "downloads/limb_guide_2.webp",
      "downloads/limb_guide_3.webp",
    ],
    link: "#",
    listed_at: "2025-12-01T12:00:00.000Z",
    asset_contents: { overlays: 1210 },
  },
  {
    id: "ov-male-limb-overlays",
    name: "Male Limb Overlays",
    price_in_credits: 0,
    members_pantry: true,
    category: "Overlays",
    description: "19 poses, each in 55 skin tones. Limelight limb overlays for male characters.",
    image_url: "downloads/male_limbs.png",
    preview_images: [
      "downloads/male_limbs.png",
      "downloads/limb_guide_1.webp",
      "downloads/limb_guide_2.webp",
      "downloads/limb_guide_3.webp",
    ],
    link: "#",
    listed_at: "2025-12-02T12:00:00.000Z",
    asset_contents: { overlays: 1045 },
  },
  {
    id: "ov-unisex-limb-overlays",
    name: "Unisex Limb Overlays",
    price_in_credits: 0,
    members_pantry: true,
    category: "Overlays",
    description: "3 poses, each in 55 skin tones. Limelight limb overlays for unisex characters.",
    image_url: "downloads/unisex_limbs.png",
    preview_images: [
      "downloads/unisex_limbs.png",
      "downloads/limb_guide_1.webp",
      "downloads/limb_guide_2.webp",
      "downloads/limb_guide_3.webp",
    ],
    link: "#",
    listed_at: "2025-12-03T12:00:00.000Z",
    asset_contents: { overlays: 165 },
  },
];

export const SAMPLE_PURCHASES = [
  { id: "pur1", product_id: "ov-phone-overlays", timestamp: Date.now() - 1000 * 60 * 60 * 24 * 2 },
  { id: "pur2", product_id: "bg-music-tour", timestamp: Date.now() - 1000 * 60 * 60 * 24 * 9 },
];

export const SAMPLE_ROADMAP = [
  {
    id: "r1",
    title: "Save-slot template",
    detail: "Menu so readers can name and reload save slots.",
    votes: 128,
    status: "progress",
    vault_link: null,
    author_name: "Cookie",
    is_anonymous: false,
  },
  {
    id: "r2",
    title: "Animated title card",
    detail: "Chapter intro: fade logo, subtitle, skip.",
    votes: 94,
    status: "planned",
    vault_link: null,
    author_name: "Maya",
    is_anonymous: false,
  },
  {
    id: "r3",
    title: "Inventory system",
    detail: "Collect, show, and spend items with a scrollable inventory overlay.",
    votes: 67,
    status: "suggestion",
    vault_link: null,
    author_name: null,
    is_anonymous: true,
  },
  {
    id: "r4",
    title: "Gender-swap helper",
    detail: "Snippet that flips dialogue and pronouns for a customizable LI.",
    votes: 210,
    status: "done",
    vault_link: "dashboard.html?asset=mg-signature-cocktail",
    author_name: "Jules",
    is_anonymous: false,
  },
];

/** @deprecated Download URLs are server-only (functions/vault-catalog.js). */
export const VAULT_PRODUCT_LINKS = {};

export const CATEGORIES = [
  "All",
  "Templates",
  "Setups",
  "Overlays",
  "Backgrounds",
  "Code",
  "Mini-games",
  "Master Exclusive",
];

/** Vault browse chips (excludes All / Code / Master Exclusive). Shared with Roadmap requests. */
export const VAULT_BROWSE_CATEGORIES = CATEGORIES.filter(
  (c) => c !== "All" && c !== "Code" && c !== "Master Exclusive"
);
